import React from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, Flex, Popover, Tag } from 'antd';

import { maxBy, sortBy, values } from 'lodash';
import { faChevronDown, faChevronUp, faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useRiskController } from 'hooks/useRiskController';

import MiniStakeVulnerabilityScale from 'pages/Project/Stakes/MiniStakeVulnerabilityScale';
import MiniIndicatorVulnerabilityScale from 'pages/Project/Indicators/MiniIndicatorVulnerabilityScale';
import { ElementPopover } from './ElementPopover';
import StakeEditionModal from 'components/Modal/StakeEditionModal';
import IndicatorEditionModal from 'components/Modal/IndicatorEditionModal';
import ConfirmModal from 'components/Modal/ConfirmModal';


import { getOpportunityScore, getRiskScore, getScoreColor, singleHue } from 'utils/ranking';
import { getItemAlteredByScenario } from 'utils/rules';
import RadialVulnerabilityGauge from 'components/charts/RadialVulnerabilityGauge';
import MiniSingleVulnerabilityScale from 'pages/Project/Risks/MiniSingleVulnerabilityScale.js';

import RiskEditionModal from 'components/Modal/RiskEditionModal';
import { useStakeController } from 'hooks/useStakeController';
import { useIndicatorController } from 'hooks/useIndicatorController';

import dayjs from 'dayjs';

import './SummaryColumn.sass';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';

export const SummaryColumn = (props: any) => {
	const { t } = useTranslation();
	const params: any = useParams();

	const { likelihoods = [], impacts = [], stakes = [], risks = [], indicators = [] } = props;

	const maxLikelihood = likelihoods && parseInt(maxBy(values(likelihoods), 'id')?.id || 3);
	const maxImpact = impacts && parseInt(maxBy(values(impacts), 'id')?.id || 3);

	const [editedItem, setEditedItem] = React.useState<any | null>(null);

	const { createRisk, updateRisk, deleteRisk } = useRiskController();
	const { createStake, updateStake, deleteStake } = useStakeController();
	const { createIndicator, updateIndicator, deleteIndicator } = useIndicatorController();

	// Filtrer les éléments par enabled !== false après application du scénario
	const filteredIndicators = (indicators || [])
		.map((c: any) => ({ ...c, altered: getItemAlteredByScenario(c, props.scenario) }))
		.filter((c: any) => c.altered.enabled !== false)
		.map((c: any) => c.altered);

	const filteredStakes = (stakes || [])
		.map((i: any) => ({ ...i, altered: getItemAlteredByScenario(i, props.scenario) }))
		.filter((i: any) => i.altered.enabled !== false)
		.map((i: any) => i.altered);

	const filteredRisks = (risks || [])
		.map((r: any) => ({ ...r, altered: getItemAlteredByScenario(r, props.scenario) }))
		.filter((r: any) => r.altered.enabled !== false)
		.map((r: any) => r.altered);

	const allRisks = filteredRisks.filter((r: any) => !r.isOpportunity);
	const allOpportunities = filteredRisks.filter((r: any) => !!r.isOpportunity);

	const askDeleteConfirmation = async (type: string, id?: string) => {

		props.pushModal(
			<ConfirmModal
				title={t('models.' + type + '.messages.delete_confirmation')}
				t={t}
				onConfirm={() => handleDelete(type, params.id, id)}
				message={t(
					'models.' + type + '.messages.delete_confirmation_explanation'
				)}
			></ConfirmModal>
		);
	};

	// Create
	const handleCreateRisk = async (isOpportunity: Boolean = false) => {
		props.pushModal(
			<RiskEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					await createRisk(params.id, risks, values, props.scenario, {}, props.scenarios || []);
					props.popModal();
				}}
				tags={props.tags}
				id={false}
				values={{
					projectId: params.id,
					isOpportunity: isOpportunity,
					impactedStakes: [],
					evaluatedAt: null
				}}
				riskCategories={props.riskCategories}
				likelihoods={props.likelihoods || []}
				impacts={props.impacts || []}
				risks={risks}
				stakes={stakes}
			></RiskEditionModal>
		);
	};

	// Create stake
	const handleCreateStake = async () => {
		props.pushModal(
			<StakeEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					let variables = {
						projectId: params.id,
						name: values.name,
						weight: values.weight || 1,
						trend: '50',
						description: values.description || '',
						evaluatedAt: values.evaluatedAt
					};

					let result = await createStake(params.id, variables, {}, props.scenario, props.scenarios || [])
					props.popModal();
				}}
				id={false}
				values={{
					name: '',
					weight: 1,
					description: '',
					evaluatedAt: null
				}}
			></StakeEditionModal>
		);
	};

	// Create indicator
	const handleCreateIndicator = async () => {
		props.pushModal(
			<IndicatorEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					let variables = {
						projectId: params.id,
						name: values.name,
						weight: values.weight || 1,
						trend: '50',
						description: values.description || '',
						evaluatedAt: values.evaluatedAt
					};

					let result = await createIndicator(params.id, variables, {}, props.scenario, props.scenarios || [])
					props.popModal();
				}}
				id={false}
				values={{
					name: '',
					weight: 1,
					description: '',
					evaluatedAt: null
				}}
			></IndicatorEditionModal>
		);
	};

	const handleDelete = async (type: string, projectId: string, id: string | undefined) => {
		const handlers = {
			'stake': () => deleteStake(projectId, id),
			'indicator': () => deleteIndicator(projectId, id),
			'risk': () => deleteRisk(projectId, id)
		};

		await handlers[type]();
	}

	// Update
	const handleUpdateStake = async (id: string, values: Object) => {
		await updateStake(id, props.stakes, props.scenario, values);
	};

	// Update
	const handleUpdateIndicator = async (id: string, values: Object) => {
		await updateIndicator(id, props.indicators, props.scenario, values);
	};

	return (
		<MaskableComponent masked={props.isMasked}>
			<aside id="SummaryColumn" className={['noselection '].join(' ')}>

				<h6 className='text-center pt-1'>{t('pages.navigator.summary.key_drivers')}</h6>
				<div
					style={{
						width: '240px',
						height: '180px',
						display: 'block',
						paddingTop: '70px',
						paddingLeft: '50px'
					}}
				>
					<RadialVulnerabilityGauge
						stakeholders={props.stakeholders}
						scenario={props.scenario}
						scenarios={props.scenarios}
						isFiltered={props.isFiltered}
					></RadialVulnerabilityGauge>
				</div>



				<div className="content">
					{props.features?.indicators && (
						<>
							<Flex className="mt-2 mb-2" vertical>
								<Flex
									className="nowrap mb-1"
									style={{ marginRight: '10px' }}
								>
									<strong>
										{t('pages.indicators.title')}
									</strong>

									<Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}} onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										props.setPreferences(
											'pages',
											'navigator.indicators',
											!props.areIndicatorsVisible
										);
									}}>
										<FontAwesomeIcon
											icon={!props.areIndicatorsVisible ? faChevronDown : faChevronUp}
										></FontAwesomeIcon>
									</Button>
								</Flex>

								<MiniIndicatorVulnerabilityScale
									scenario={props.scenario}
									scenarios={props.scenarios}
									indicators={filteredIndicators}
								></MiniIndicatorVulnerabilityScale>
							</Flex>
							{props.areIndicatorsVisible && <div className="text-left">
								{filteredIndicators && filteredIndicators.length ? (
									sortBy(filteredIndicators || [], 'name').map(
										(alteredItem: any, index: number) => {
											// L'élément original pour l'édition
											const originalItem = (indicators || []).find((c: any) => c.id === alteredItem.id);

											return (
												<Popover
													key={index}
													trigger={'click'}
													placement="topLeft"
													styles={{ root: { maxWidth: '400px' } }}
													title={
														<div>
															{alteredItem.name || "No name"}{' '}
															<FontAwesomeIcon
																style={{
																	marginLeft:
																		'0.5rem',
																	fontSize: '1.2rem',
																	cursor: 'pointer'
																}}
																icon={faEdit}
																onClick={() =>
																	setEditedItem(originalItem)
																}
															/>
														</div>
													}
													content={
														<ElementPopover
															{...alteredItem}
															originalElement={originalItem}
															onUpdate={
																handleUpdateIndicator
															}
															scenario={props.scenario}
															scenarios={props.scenarios}
														></ElementPopover>
													}
												>
													<Tag
														className={[
															'tag ImpactedIndicator unselected'
														].join(' ')}
													>
														<div
															className={
																'Trend v' +
																alteredItem.trend
															}
															style={
																alteredItem.trend ==
																	'50'
																	? {}
																	: {
																		background:
																			getScoreColor(
																				alteredItem.trend
																			)
																	}
															}
														></div>
														<span className="tag__label" title={alteredItem.name || "No name"}>{alteredItem.name || "No name"}</span>
													</Tag>
												</Popover>
											);
										}
									)
								) : (
									<div
										className="text-center"
										style={{ color: 'var(--color-text-secondary)' }}
									>
										{t('models.indicator.no_x')}
									</div>
								)}

								{
									<Tag
										onClick={handleCreateIndicator}
										className={'tag ImpactedStake unselected'}
										style={{ margin: 'auto' }}
									>
										+
									</Tag>
								}
							</div>}
						</>
					)}

					{props.features?.stakes && (
						<>
							<Flex className="mt-2 mb-2" vertical>
								<Flex
									className="nowrap mb-1"
									justify="space-between"
									style={{ marginRight: '10px' }}
								>
									<strong>
										{t('pages.stakes.title')}
									</strong>

									<Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}} onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										props.setPreferences(
											'pages',
											'navigator.stakes',
											!props.areStakesVisible
										);
									}}>
										<FontAwesomeIcon
											icon={!props.areStakesVisible ? faChevronDown : faChevronUp}
										></FontAwesomeIcon>
									</Button>
								</Flex>

								<MiniStakeVulnerabilityScale
									scenario={props.scenario}
									scenarios={props.scenarios}
									stakes={filteredStakes}
								></MiniStakeVulnerabilityScale>
							</Flex>
							{props.areStakesVisible && <div className="text-left">
								{filteredStakes && filteredStakes.length ? (
									sortBy(filteredStakes || [], 'name').map(
										(alteredItem: any, index: number) => {
											// L'élément original pour l'édition
											const originalItem = (stakes || []).find((i: any) => i.id === alteredItem.id);

											return (
												<Popover
													key={index}
													trigger={'click'}
													placement="topLeft"
													styles={{ root: { maxWidth: '400px' } }}
													title={
														<div>
															{alteredItem.name || "No name"}{' '}
															<FontAwesomeIcon
																style={{
																	marginLeft:
																		'0.5rem',
																	fontSize: '1.2rem',
																	cursor: 'pointer'
																}}
																icon={faEdit}
																onClick={() =>
																	setEditedItem(originalItem)
																}
															/>
														</div>
													}
													content={
														<ElementPopover
															{...alteredItem}
															originalElement={originalItem}
															scenario={props.scenario}
															scenarios={props.scenarios}
															onUpdate={
																handleUpdateStake
															}
														></ElementPopover>
													}
												>
													<Tag
														className={[
															'tag ImpactedStake unselected'
														].join(' ')}
													>
														<div
															className={
																'Trend v' +
																alteredItem.trend
															}
															style={
																alteredItem.trend ==
																	'50'
																	? {}
																	: {
																		background:
																			getScoreColor(
																				alteredItem.trend
																			)
																	}
															}
														></div>
														<span className="tag__label" title={alteredItem.name || "No name"}>{alteredItem.name || "No name"}</span>
													</Tag>
												</Popover>
											);
										}
									)
								) : (
									<div
										className="text-center"
										style={{ color: 'var(--color-text-secondary)' }}
									>
										{t('models.stake.no_x')}
									</div>
								)}
								{
									<Tag
										onClick={handleCreateStake}
										className={'tag ImpactedStake unselected'}
									>
										+
									</Tag>
								}
							</div>}
						</>
					)}

					{props.features?.risks && (
						<>
							<Flex className="mt-2 mb-2" vertical>
								<Flex
									className="nowrap mb-1"
									justify="space-between"
									style={{ marginRight: '10px' }}
								>
									<strong>
										{t('models.risk.name_plural')}
									</strong>

									<Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}} onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										props.setPreferences(
											'pages',
											'navigator.risks',
											!props.areRisksVisible
										);
									}}>
										<FontAwesomeIcon
											icon={!props.areRisksVisible ? faChevronDown : faChevronUp}
										></FontAwesomeIcon>
									</Button>
								</Flex>

								<MiniSingleVulnerabilityScale
									scenario={props.scenario}
									scenarios={props.scenarios}
									elements={allRisks}
									maxLikelihood={maxLikelihood}
									maxImpact={maxImpact}
								></MiniSingleVulnerabilityScale>
							</Flex>
							{props.areRisksVisible && <div className="text-left">
								{allRisks && allRisks.length ? (
									sortBy(allRisks || [], 'name').map(
										(alteredItem: any, index: number) => {
											// L'élément original pour l'édition
											const originalItem = (risks || []).find((r: any) => r.id === alteredItem.id);

											return (
												<Popover
													key={index}
													trigger={'click'}
													placement="topLeft"
													styles={{ root: { maxWidth: '400px' } }}
													title={
														<div>
															{alteredItem.name || "No name"}{' '}
															{
																<FontAwesomeIcon
																	style={{
																		marginLeft:
																			'0.5rem',
																		fontSize: '1.2rem',
																		cursor: 'pointer'
																	}}
																	icon={faEdit}
																	onClick={() =>
																		setEditedItem(originalItem)
																	}
																/>}
														</div>
													}
													content={
														<ElementPopover
															{...alteredItem}
															originalElement={originalItem}
															onUpdate={(id, values) => updateRisk(id, risks, props.scenario, values)}
															scenario={props.scenario}
															scenarios={props.scenarios}
															likelihoods={props.likelihoods}
															impacts={props.impacts}

														></ElementPopover>
													}
												>
													<Tag
														className={[
															'tag ImpactedIndicator unselected'
														].join(' ')}
													>
														<div
															className={
																'Trend v'
															}
															style={
																alteredItem.trend ==
																	'50'
																	? {}
																	: {
																		background:
																			singleHue(
																				getRiskScore([alteredItem], maxLikelihood, maxImpact, props.riskCategories),
																				false
																			)
																	}
															}
														></div>
														<span className="tag__label" title={alteredItem.name || "No name"}>{alteredItem.name || "No name"}</span>
													</Tag>
												</Popover>
											);
										}
									)
								) : (
									<div
										className="text-center"
										style={{ color: 'var(--color-text-secondary)' }}
									>
										{t('models.risk.messages.no_x')}
									</div>
								)}

								{
									<Tag
										onClick={() => handleCreateRisk(false)}
										className={'tag ImpactedStake unselected'}
										style={{ margin: 'auto' }}
									>
										+
									</Tag>
								}
							</div>}

							<Flex className="mt-2 mb-2" vertical>
								<Flex
									className="nowrap mb-1"
									justify="space-between"
									style={{ marginRight: '10px' }}
								>
									<strong>
										{t('models.risk.name_plural_positive')}
									</strong>

									<Button shape="circle" className="mini ml-auto" onDoubleClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}} onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										props.setPreferences(
											'pages',
											'navigator.opportunities',
											!props.areOpportunitiesVisible
										);
									}}>
										<FontAwesomeIcon
											icon={!props.areOpportunitiesVisible ? faChevronDown : faChevronUp}
										></FontAwesomeIcon>
									</Button>
								</Flex>

								<MiniSingleVulnerabilityScale
									scenario={props.scenario}
									scenarios={props.scenarios}
									positive={true}
									elements={allOpportunities}
									maxLikelihood={maxLikelihood}
									maxImpact={maxImpact}
								></MiniSingleVulnerabilityScale>
							</Flex>
							{props.areOpportunitiesVisible && <div className="text-left">
								{allOpportunities && allOpportunities.length ? (
									sortBy(allOpportunities || [], 'name').map(
										(alteredItem: any, index: number) => {
											// L'élément original pour l'édition
											const originalItem = (risks || []).find((r: any) => r.id === alteredItem.id);

											return (
												<Popover
													key={index}
													trigger={'click'}
													title={
														<div>
															{alteredItem.name || "No name"}{' '}
															<FontAwesomeIcon
																style={{
																	marginLeft:
																		'0.5rem',
																	fontSize: '1.2rem',
																	cursor: 'pointer'
																}}
																icon={faEdit}
																onClick={() =>
																	setEditedItem(originalItem)
																}
															/>
														</div>
													}
													content={
														<ElementPopover
															{...alteredItem}
															originalElement={originalItem}
															onUpdate={(id, values) => updateRisk(id, risks, props.scenario, values)}
															scenario={props.scenario}
															scenarios={props.scenarios}
															likelihoods={props.likelihoods}
															impacts={props.impacts}
														></ElementPopover>
													}
												>
													<Tag
														className={[
															'tag ImpactedIndicator unselected'
														].join(' ')}
													>
														<div
															className={
																'Trend v'
															}
															style={
																alteredItem.trend ==
																	'50'
																	? {}
																	: {
																		background:
																			singleHue(
																				getOpportunityScore([alteredItem], maxLikelihood, maxImpact, props.riskCategories),
																				true
																			)
																	}
															}
														></div>
														<span className="tag__label" title={alteredItem.name || "No name"}>{alteredItem.name || "No name"}</span>
													</Tag>
												</Popover>
											);
										}
									)
								) : (
									<div
										className="text-center"
										style={{ color: 'var(--color-text-secondary)' }}
									>
										{t('models.risk.messages.no_x_positive')}
									</div>
								)}

								{
									<Tag
										onClick={() => handleCreateRisk(true)}
										className={'tag ImpactedStake unselected'}
										style={{ margin: 'auto' }}
									>
										+
									</Tag>
								}
							</div>}
						</>
					)}
				</div>



				{editedItem && editedItem.__typename == 'Stake' && (
					<StakeEditionModal
						width={"70%"}
						onHide={() => {
							setEditedItem(null);
						}}
						onDelete={() => {
							askDeleteConfirmation('stake', editedItem.id);
							setEditedItem(null);
						}}
						onSave={(values: Object) => {
							handleUpdateStake(editedItem.id, values);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						values={{
							name: editedItem?.name,
							weight: editedItem?.weight || 1,
							description: editedItem?.description || '',
							reference: editedItem?.reference,
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null
						}}
					></StakeEditionModal>
				)}

				{editedItem && editedItem.__typename == 'Indicator' && (
					<IndicatorEditionModal
						width={"70%"}
						onHide={() => {
							setEditedItem(null);
						}}
						onDelete={() => {
							askDeleteConfirmation('indicator', editedItem.id);
							setEditedItem(null);
						}}
						onSave={(values: Object) => {
							handleUpdateIndicator(editedItem.id, values);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						values={{
							name: editedItem?.name,
							weight: editedItem?.weight || 1,
							description: editedItem?.description || '',
							reference: editedItem?.reference,
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null
						}}
					></IndicatorEditionModal>
				)}

				{editedItem && editedItem.__typename == 'Risk' && (
					<RiskEditionModal
						onHide={() => {
							setEditedItem(null);
						}}
						onDelete={() => {
							askDeleteConfirmation('risk', editedItem.id);
							setEditedItem(null);
						}}
						onSave={(values: Object) => {
							updateRisk(
								editedItem.id,
								risks,
								props.scenario,
								values
							);
							setEditedItem(null);
						}}
						tags={props.tags}
						id={editedItem?.id}
						likelihoods={props.likelihoods || []}
						impacts={props.impacts || []}
						values={{
							name: editedItem?.name,
							impact: editedItem?.impact || 1,
							likelihood: editedItem?.likelihood || 1,
							description: editedItem?.description || '',
							riskCategoryId: editedItem?.riskCategoryId,
							comment: editedItem?.comment || '',
							isOpportunity: editedItem?.isOpportunity || false,
							openingIds: (editedItem?.openingLinks || []).map(l => l.openingId),
							taggings: editedItem?.taggings?.map(t => t.tagId) || [],
							reference: editedItem?.reference,
							evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null,
							impactedStakes: (editedItem?.impactedStakes || []).map(ii => ii.stakeId),
						}}
						riskCategories={props.riskCategories}
						risks={risks.filter(r => r.id != editedItem?.id)}
						stakes={stakes}
					></RiskEditionModal>
				)}

				<div style={{ position: "absolute" }}>
					{props.children}
				</div>

			</aside>
		</MaskableComponent>
	);
};
